import { useMemo } from "react";
import { Quantity } from "../../inputs/Quantity";
import {
  ResolvedComboItem,
  ResolvedComboItemFixed,
  ResolvedComboItemSelect,
} from "../../../../interfaces/domain/menu";
import styles from "./ComboSelector.module.scss";

interface Props {
  combo: { items: ResolvedComboItem[] };
  quantity: number;
  selection: Record<string, number>;
  setSelection: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  readOnly?: boolean;
}

export const ComboSelector = ({ combo, selection, setSelection, readOnly = false }: Props) => {
  if (!combo) return null;

  const fixedItems = combo.items.filter((i): i is ResolvedComboItemFixed => i.type === "fixed");
  const selectItem = combo.items.find((i): i is ResolvedComboItemSelect => i.type === "select");

  if (!fixedItems.length && !selectItem) return null;

  const products = selectItem?.choices ?? [];
  const required = selectItem?.quantity ?? 0;

  const totalSelected = useMemo(
    () => Object.values(selection).reduce((acc: number, val: number) => acc + val, 0),
    [selection]
  );

  const faltan = required - totalSelected;

  const handleUpdate = (id: string, newQty: number) => {
    setSelection((prev) => {
      const current = prev[id] || 0;
      const prevTotal = Object.values(prev).reduce((acc: number, val: number) => acc + val, 0);
      if (newQty > current && prevTotal >= required) return prev;
      const next = { ...prev };
      if (newQty <= 0) delete next[id];
      else next[id] = newQty;
      return next;
    });
  };

  return (
    <div className={styles.comboContainer}>
      {fixedItems.length > 0 && (
        <div className={styles.fixedSection}>
          <h3 className={styles.sectionTitle}>Incluye</h3>
          <div className={styles.fixedList}>
            {fixedItems.map((item) => (
              <div key={item.productId} className={styles.fixedRow}>
                <span className={styles.fixedQty}>
                  {item.quantity > 1 ? `${item.quantity}×` : "•"}
                </span>
                <span className={styles.fixedName}>{item.title ?? "Producto"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectItem && (
        <>
          <h3 className={styles.sectionTitle}>{selectItem.label || "Elegí una opción"}</h3>
          {!readOnly && (
            <div className={`${styles.feedback} ${faltan > 0 ? styles.pending : styles.complete}`}>
              {faltan > 0 ? `Faltan elegir ${faltan}` : "Selección completa ✔"}
            </div>
          )}
          <div className={styles.productList}>
            {products.map((p) => {
              const count = selection[p.id] || 0;
              const disableAdd = totalSelected >= required && count === 0;
              return (
                <div key={p.id} className={styles.productRow}>
                  <span className={styles.productName}>{p.title}</span>
                  {!readOnly && (
                    count === 0 ? (
                      <button
                        className={styles.selectButton}
                        onClick={() => handleUpdate(p.id, 1)}
                        disabled={disableAdd}
                        style={{ opacity: disableAdd ? 0.5 : 1 }}
                      >
                        Seleccionar
                      </button>
                    ) : (
                      <div className={styles.containerQuantity}>
                        <Quantity
                          quantity={count}
                          setQuantity={(val) => {
                            if (val > count) handleUpdate(p.id, count + 1);
                            else handleUpdate(p.id, count - 1);
                          }}
                          title={p.title}
                          price={0}
                          photo={p.image_url}
                        />
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
